/* eslint-disable @typescript-eslint/no-explicit-any */
// src/utils/logger.ts

/**
 * 定义日志级别
 * DEBUG: 最详细，仅开发环境使用
 * INFO: 正常运行时信息
 * WARN: 警告，程序可能存在问题
 * ERROR: 错误，程序运行失败
 * NONE: 不输出任何日志
 */
export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'NONE'

/**
 * 日志工具配置项接口
 */
export interface LoggerOptions {
  /** 日志级别，低于此级别的日志将不被输出 */
  level: LogLevel
  /** 日志前缀，用于区分不同模块或应用 */
  prefix: string
}
// src/utils/logger.ts

// ... (接上面的 LogLevel 和 LoggerOptions 定义)

export class Logger {
  private level: LogLevel
  private prefix: string

  // 映射日志级别到数字，用于比较
  private readonly levelsMap: Record<LogLevel, number> = {
    DEBUG: 1,
    INFO: 2,
    WARN: 3,
    ERROR: 4,
    NONE: 5,
  }

  /**
   * 构造函数
   * @param options 可选配置，未提供时将根据 NODE_ENV 默认设置
   */
  constructor(options: Partial<LoggerOptions> = {}) {
    // 默认级别：开发环境为 DEBUG，生产环境为 INFO
    // Vite 浏览器环境用 import.meta.env.PROD，而非依赖构建期注入的 process.env
    this.level = options.level || (import.meta.env.PROD ? 'INFO' : 'DEBUG')
    this.prefix = options.prefix || '[APP]'
  }

  /**
   * 私有方法：处理实际的日志输出逻辑和级别判断
   */
  private output(level: LogLevel, ...messages: any[]): void {
    // 1. 检查日志级别：如果当前消息级别低于配置级别，则跳过
    if (this.levelsMap[level] < this.levelsMap[this.level]) {
      return
    }

    // 2. 确定使用的 console 方法
    let consoleMethod: 'log' | 'info' | 'warn' | 'error' | 'debug'
    switch (level) {
      case 'INFO':
        consoleMethod = 'info'
        break
      case 'WARN':
        consoleMethod = 'warn'
        break
      case 'ERROR':
        consoleMethod = 'error'
        break
      case 'DEBUG':
        consoleMethod = 'debug'
        break
      default:
        consoleMethod = 'log'
    }

    // 3. 应用样式（可选，让日志更醒目）
    const style =
      level === 'ERROR'
        ? 'color: red; font-weight: bold;'
        : level === 'WARN'
          ? 'color: orange;'
          : level === 'INFO'
            ? 'color: blue;'
            : 'color: gray;'

    // 4. 输出格式：%c 用于应用样式，后面跟前缀和级别
    // 注意：messages 数组中可以包含对象、数组等
    // eslint-disable-next-line no-console
    console[consoleMethod](`%c${this.prefix} [${level}]`, style, ...messages)
  }

  // 5. 公共日志输出方法
  public debug(...messages: any[]): void {
    this.output('DEBUG', ...messages)
  }

  public info(...messages: any[]): void {
    this.output('INFO', ...messages)
  }

  public warn(...messages: any[]): void {
    this.output('WARN', ...messages)
  }

  public error(...messages: any[]): void {
    this.output('ERROR', ...messages)
  }

  // log 方法可以作为 INFO 的别名
  public log(...messages: any[]): void {
    this.output('INFO', ...messages)
  }
}

export const logger = new Logger({ prefix: '[FE-CORE]' })
